import React, { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useOkapiKy } from "@folio/stripes/core";
import { Button, Loading, MessageBanner } from "@folio/stripes/components";
import { FormattedMessage, useIntl } from "react-intl";
import { fetchSyncStatus, syncCollectionsToLegalDeposits, syncOnixToLegalDeposits, syncPersonsToLegalDeposits, syncProductsToLegalDeposits, syncTitlesToLegalDeposits, } from "../../api/adminService";
import styles from "./LegalDepositsSynchronize.module.css";
var SYNC_STATUS_QUERY_KEY = ["agency", "admin", "syncStatus"];
var SYNC_TYPES = [
    {
        key: "ONIX",
        labelId: "ui-br-agency.admin.sync.onix.button",
        trigger: syncOnixToLegalDeposits,
    },
    {
        key: "COLLECTIONS",
        labelId: "ui-br-agency.admin.sync.collections.button",
        trigger: syncCollectionsToLegalDeposits,
    },
    {
        key: "PERSONS",
        labelId: "ui-br-agency.admin.sync.persons.button",
        trigger: syncPersonsToLegalDeposits,
    },
    {
        key: "TITLES",
        labelId: "ui-br-agency.admin.sync.titles.button",
        trigger: syncTitlesToLegalDeposits,
    },
    {
        key: "PRODUCTS",
        labelId: "ui-br-agency.admin.sync.products.button",
        trigger: syncProductsToLegalDeposits,
    },
];
var SyncStatusIndicator = function (_a) {
    var _b, _c, _d;
    var status = _a.status;
    var formatMessage = useIntl().formatMessage;
    if (!status || status.status === "IDLE") {
        return null;
    }
    if (status.status === "RUNNING") {
        var percent = status.totalCount > 0
            ? Math.round((status.syncedCount / status.totalCount) * 100)
            : 0;
        return (React.createElement("span", { className: styles.statusText },
            React.createElement(Loading, null),
            formatMessage({ id: "ui-br-agency.admin.sync.status.running" }, {
                syncedCount: status.syncedCount,
                totalCount: status.totalCount,
                percent: percent,
                seconds: (_b = status.durationSeconds) !== null && _b !== void 0 ? _b : 0,
            })));
    }
    if (status.status === "COMPLETED") {
        return (React.createElement("span", { className: "".concat(styles.statusText, " ").concat(styles.statusDone) }, formatMessage({ id: "ui-br-agency.admin.sync.status.completed" }, {
            syncedCount: status.syncedCount,
            seconds: (_c = status.durationSeconds) !== null && _c !== void 0 ? _c : 0,
        })));
    }
    return (React.createElement("span", { className: "".concat(styles.statusText, " ").concat(styles.statusError) }, formatMessage({ id: "ui-br-agency.admin.sync.status.failed" }, { error: (_d = status.errorMessage) !== null && _d !== void 0 ? _d : "" })));
};
export var LegalDepositsSynchronize = function () {
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    var _a = useQuery(SYNC_STATUS_QUERY_KEY, function () { return fetchSyncStatus(ky); }, {
        refetchInterval: function (data) {
            return (data === null || data === void 0 ? void 0 : data.some(function (item) { return item.status === "RUNNING"; })) ? 2000 : false;
        },
    }), statuses = _a.data, isStatusLoadError = _a.isError;
    var statusByType = useMemo(function () {
        var map = new Map();
        (statuses !== null && statuses !== void 0 ? statuses : []).forEach(function (item) { return map.set(item.type, item); });
        return map;
    }, [statuses]);
    var triggerMutation = useMutation({
        mutationFn: function (config) { return config.trigger(ky); },
        onSettled: function () { return queryClient.invalidateQueries(SYNC_STATUS_QUERY_KEY); },
    });
    return (React.createElement(React.Fragment, null,
        React.createElement(FormattedMessage, { id: "ui-br-agency.admin.sync.legaldeposits.title", tagName: "h1" }),
        React.createElement(MessageBanner, { type: "warning" },
            React.createElement(FormattedMessage, { id: "ui-br-agency.admin.sync.order.info" })),
        isStatusLoadError && (React.createElement(MessageBanner, { type: "error" },
            React.createElement(FormattedMessage, { id: "ui-br-agency.admin.sync.status.loadError" }))),
        React.createElement("div", { className: styles.syncList }, SYNC_TYPES.map(function (config) {
            var _a, _b;
            var status = statusByType.get(config.key);
            var isRunning = (status === null || status === void 0 ? void 0 : status.status) === "RUNNING";
            var isTriggering = triggerMutation.isLoading &&
                ((_a = triggerMutation.variables) === null || _a === void 0 ? void 0 : _a.key) === config.key;
            var isTriggerError = triggerMutation.isError &&
                ((_b = triggerMutation.variables) === null || _b === void 0 ? void 0 : _b.key) === config.key;
            return (React.createElement("div", { key: config.key, className: styles.syncRow },
                React.createElement(Button, { buttonStyle: "primary", onClick: function () { return triggerMutation.mutate(config); }, disabled: isRunning || isTriggering, marginBottom0: true },
                    React.createElement(FormattedMessage, { id: config.labelId })),
                isTriggerError ? (React.createElement("span", { className: "".concat(styles.statusText, " ").concat(styles.statusError) },
                    React.createElement(FormattedMessage, { id: "ui-br-agency.admin.sync.trigger.error" }))) : (React.createElement(SyncStatusIndicator, { status: status }))));
        }))));
};
